import { escapeHtml } from '../format';
import { requireElement, prefersReducedMotion, type MountedScreen } from './dom';
import { icon } from './icons';
import { STAGE_ANNOUNCEMENTS, STAGE_LABELS, STAGE_TITLES, stageQuip, type ProgressStage } from './progress-model';

/**
 * "읽는 중" screen (spec §6.2-2): progress bar, playful rotating copy (hidden
 * from screen readers) and one polite announcement per stage.
 */

export interface ProgressScreenOptions {
  readonly stage: ProgressStage;
  /** Overrides the stage title until the stage changes. */
  readonly title?: string;
  readonly reminder: string;
  /** Omit to hide the cancel button. */
  readonly onCancel?: () => void;
}

export interface ProgressScreen extends MountedScreen {
  setStage(stage: ProgressStage): void;
  /** 0-100, or null for an indeterminate bar. */
  setPercent(percent: number | null): void;
}

export function mountProgressScreen(stage: HTMLElement, options: ProgressScreenOptions): ProgressScreen {
  const section = document.createElement('section');
  section.className = 'krc-screen krc-progress';
  section.setAttribute('aria-labelledby', 'krc-progress-title');
  section.innerHTML = `
    <div class="krc-card krc-progress-card">
      <div class="krc-progress-art" aria-hidden="true">
        <span class="krc-paper"><i></i><i></i><i></i><b>A+</b></span>
      </div>
      <h2 class="krc-screen-title" id="krc-progress-title" tabindex="-1"></h2>
      <p class="krc-progress-quip" aria-hidden="true"></p>
      <div class="krc-progress-row" aria-hidden="true"><span data-progress-label></span><strong data-progress-value></strong></div>
      <div class="krc-progress-bar" role="progressbar" aria-labelledby="krc-progress-title" aria-valuemin="0" aria-valuemax="100"><span></span></div>
      <p class="krc-sr-only" aria-live="polite" data-progress-live></p>
      <p class="krc-progress-reminder">${icon('lock')}<span>${escapeHtml(options.reminder)}</span></p>
      ${options.onCancel ? '<button class="button button-soft krc-progress-cancel" type="button" data-progress-cancel>그만두기</button>' : ''}
    </div>`;
  stage.append(section);

  const title = requireElement<HTMLElement>(section, '#krc-progress-title');
  const quip = requireElement<HTMLElement>(section, '.krc-progress-quip');
  const label = requireElement<HTMLElement>(section, '[data-progress-label]');
  const value = requireElement<HTMLElement>(section, '[data-progress-value]');
  const bar = requireElement<HTMLElement>(section, '.krc-progress-bar');
  const fill = requireElement<HTMLElement>(bar, 'span');
  const live = requireElement<HTMLElement>(section, '[data-progress-live]');
  section.querySelector('[data-progress-cancel]')?.addEventListener('click', () => options.onCancel?.());

  let current: ProgressStage | null = null;
  let tick = 0;
  const rotate = window.setInterval(() => {
    if (!current) return;
    tick += 1;
    quip.textContent = stageQuip(current, tick);
  }, prefersReducedMotion() ? 3_200 : 1_900);

  const setStage = (next: ProgressStage) => {
    if (next === current) return;
    current = next;
    tick = 0;
    section.dataset.stage = next;
    title.textContent = STAGE_TITLES[next];
    label.textContent = STAGE_LABELS[next];
    quip.textContent = stageQuip(next, 0);
    live.textContent = STAGE_ANNOUNCEMENTS[next];
  };

  const setPercent = (percent: number | null) => {
    if (percent === null) {
      bar.classList.add('is-indeterminate');
      bar.removeAttribute('aria-valuenow');
      bar.setAttribute('aria-valuetext', '진행 중');
      value.textContent = '';
      fill.style.width = '';
      return;
    }
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    bar.classList.remove('is-indeterminate');
    bar.setAttribute('aria-valuenow', String(clamped));
    bar.removeAttribute('aria-valuetext');
    value.textContent = `${clamped}%`;
    fill.style.width = `${clamped}%`;
  };

  setStage(options.stage);
  if (options.title) title.textContent = options.title;
  setPercent(options.stage === 'analyzing' || options.stage === 'downloading' ? null : 0);

  return {
    heading: title,
    setStage,
    setPercent,
    dispose() {
      window.clearInterval(rotate);
      section.remove();
    },
  };
}
